import React, { useContext, useState } from "react";
import "./styles.css";
import { PlayerAction } from "../../../types";
import { constants } from "../../../constants";
import Waitable from "../../common/waitable/waitable";
import { GameStatus } from "../../../types/game-status";
import { useAppSelector } from "../../../redux/store";
import FormPage from "../../common/formpage/formpage";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { useWaitableForm } from "../../../hooks/useWaitableForm";
import { GameContext } from "../../../contexts";

import { useTranslation } from "react-i18next";
import PrevSubmission from "../../common/prev-submission/prev-submission";
import { selectAccessibility2 } from "../../../redux/selectors";

const StoryTitle = () => {
  const { submit } = useContext(GameContext);

  const gameState = useAppSelector((state) => state.game);
  const playerState = useAppSelector((state) => state.player);
  const accessibility = useAppSelector(selectAccessibility2);

  const [title, setTitle] = useState("");

  const [flagged, setFlagged] = useState(new Set<number>());

  const { t } = useTranslation(["game", "common"]);

  function handleTitle(event: React.ChangeEvent<HTMLInputElement>) {
    setTitle(event.currentTarget.value);
    return event.currentTarget.value;
  }

  function handleSubmit(): PlayerAction {
    return {
      body: {
        story: {
          id: playerState.player?.current_story?.id,
          meta: {
            title: title,
          },
        },
        flagged_submissions: Array.from(flagged),
      },
      code: "story/title",
    };
  }

  function handleFlag(id: number, checked: boolean) {
    setFlagged((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }

  const { isReady } = useFormValidation({
    validationRules: [{ validate: () => title.trim().length > 0 }],
    dependencies: [title],
  });

  const { waitableProps, formPageProps } = useWaitableForm({
    gameSubmit: handleSubmit,
    waitingFor: [GameStatus.GameStatusPostStoryTitle],
    waitHeader: t("storyTitle.headers.waiting"),
    titles: [t("storyTitle.headers.default")],
    validation: { isReady },
    subHeaders:
      !gameState.game?.settings.solo_writing
        ? [t("storyTitle.subheaders.default")]
        : [t("storyTitle.subheaders.defaultSolo")],
  });

  return (
    <Waitable
      playerActionHandler={submit}
      waitingFor={[
        GameStatus.GameStatusPostStoryTitle,
        GameStatus.GameStatusPostCensoring,
      ]}
      waitHeader={t("storyTitle.headers.waiting")}
    >
      <FormPage {...formPageProps}>
        <div className="flex flex-col relative px-2 divide-black divide-y">
          <div className="pt-2 bg-gray-light top-0 sticky z-10 flex flex-col">
            <div className="flex flex-row items-stretch">
              <label
                className="text-xl self-center text-center float-left w-1/4"
                htmlFor="story-title-field"
              >
                {t("label.title", { ns: "common" })}:
              </label>
              <div className="flex-grow pr-8">
                <input
                  autoFocus={true}
                  autoComplete={"off"}
                  className="text-xl block bg-gray-drk w-full common-shadow"
                  id="story-title-field"
                  minLength={1}
                  maxLength={60}
                  type="text"
                  value={title}
                  onChange={handleTitle}
                  required
                />
              </div>
            </div>
          </div>

          <div
            className="mt-2 break-words whitespace-pre-wrap normal-case divide-y divide-black divide-dashed space-y-2 [&_*]:text-xl"
            style={constants.accessibleStyle(accessibility)}
          >
            {playerState.player?.current_story?.submissions.map<React.ReactNode>(
              (s, idx) => {
                return (
                  <div key={"submission-" + idx}>
                    <PrevSubmission
                      variant="flag"
                      submission={s}
                      onToggle={(id, checked) => handleFlag(id, checked)}
                    />
                  </div>
                );
              }
            )}
          </div>
        </div>
      </FormPage>
    </Waitable>
  );
};
export default StoryTitle;
